import { useCallback, useMemo } from 'react'

import type {
  CreateCallback,
  ScheduleOption,
  UpdateCallback,
  UpdateCallbackOptions,
} from '../../interfaces/types'
import AppActionCreators from '../action-creators/AppActionCreators'
import { QueueingTypes } from '../AppConstants'
import { useChannelGroupsContext } from '../hooks/channelGroupsContext'

type QueueingType = (typeof QueueingTypes)[keyof typeof QueueingTypes]

const SchedulePostQueueingType: Map<ScheduleOption | undefined, QueueingType> =
  new Map([
    ['FixedTime', QueueingTypes.CUSTOM],
    ['Now', QueueingTypes.NOW],
    ['Prioritize', QueueingTypes.NEXT],
  ])

const SaveAsDraftQueueingType: Map<ScheduleOption | undefined, QueueingType> =
  new Map([
    ['FixedTime', QueueingTypes.CUSTOM_DRAFT],
    ['Now', QueueingTypes.ADD_DRAFT],
  ])

export const useSaveCallbacks = (
  option: ScheduleOption = 'Queue',
  scheduledAt: number | null = null,
): {
  schedulePost: CreateCallback
  saveAsDraft: CreateCallback
  saveAndRequestApproval: CreateCallback
  updatePost: UpdateCallback
} => {
  const { selectedChannelGroupIds } = useChannelGroupsContext()

  const schedulePost = useCallback(
    (options?: { willCreateAnotherPost?: boolean }) => {
      const queuingType =
        SchedulePostQueueingType.get(option) ?? QueueingTypes.QUEUE

      return AppActionCreators.saveDrafts(queuingType, {
        shouldSkipEmptyTextAlert: false,
        hasCampaignsToTagsFeature: true,
        customScheduleTime: scheduledAt,
        channelGroupIds: Array.from(selectedChannelGroupIds),
        willCreateAnotherPost: options?.willCreateAnotherPost,
      })
    },
    [option, scheduledAt, selectedChannelGroupIds],
  )

  const saveAsDraft = useCallback(() => {
    const queuingType =
      SaveAsDraftQueueingType.get(option) ?? QueueingTypes.ADD_DRAFT

    return AppActionCreators.saveDrafts(queuingType, {
      hasCampaignsToTagsFeature: true,
      customScheduleTime: scheduledAt,
      channelGroupIds: Array.from(selectedChannelGroupIds),
    })
  }, [option, scheduledAt, selectedChannelGroupIds])

  const saveAndRequestApproval = useCallback(() => {
    const queuingType =
      SaveAsDraftQueueingType.get(option) ?? QueueingTypes.ADD_DRAFT

    return AppActionCreators.saveDrafts(queuingType, {
      shouldSkipEmptyTextAlert: false,
      hasCampaignsToTagsFeature: true,
      customScheduleTime: scheduledAt,
      // we can send anything here, as long as it's set it will work
      // we might want to support variations in the future
      approvalMode: 'ADD_TO_DRAFTS_REQUESTING_APPROVAL',
      channelGroupIds: Array.from(selectedChannelGroupIds),
    })
  }, [option, scheduledAt, selectedChannelGroupIds])

  const updatePost = useCallback(
    ({
      currentStatus,
      newStatus,
      scheduleSelectorChange,
    }: UpdateCallbackOptions) => {
      const queuingType = QueueingTypes.SAVE
      const shouldSkipEmptyTextAlert =
        currentStatus === 'draft' || newStatus === 'draft'

      return AppActionCreators.saveDrafts(queuingType, {
        shouldSkipEmptyTextAlert,
        hasCampaignsToTagsFeature: true,
        customScheduleTime: scheduledAt,
        channelGroupIds: Array.from(selectedChannelGroupIds),
        statusChange: newStatus,
        scheduleSelectorChange,
      })
    },
    [scheduledAt, selectedChannelGroupIds],
  )

  return useMemo(
    () => ({
      schedulePost,
      saveAsDraft,
      saveAndRequestApproval,
      updatePost,
    }),
    [schedulePost, saveAsDraft, saveAndRequestApproval, updatePost],
  )
}
